/* ==========================================================================
   flash-offer.js — "50% off if you order within 30 minutes" popup.
   The popup is shown automatically at most once per browser every 6 hours
   (on a click to a product / collection, or 3 s after landing on a
   product page from another site, e.g. an ad). Everything else is manual:
   after an offer ends a "Reclaim" button asks for an extension (fake
   review, 5 s) and then re-offers the discount for 10 minutes only.
   Flow: click on any link to a product / collection -> popup -> Accept ->
   popup dissolves, fireworks reveal the countdown, every price shows
   old (struck through) + new (shiny, darker) price. The discount is real:
   it is a Shopify code applied through the cart.js promo mechanism
   (sessionStorage "apykidz_promo" -> /discount/CODE?redirect=/checkout).
   Config comes from window.APYKIDZ_FLASH = { code, minutes, percent }.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = window.APYKIDZ_FLASH || {};
  var CODE = CFG.code;
  if (!CODE) return;
  var MINUTES = CFG.minutes || 30;
  var EXT_MINUTES = CFG.extMinutes || 10;
  var AUTO_GAP_MS = (CFG.autoHours || 6) * 3600 * 1000;
  var EXT_WAIT_MS = 10 * 60 * 1000;
  var ASK_DELAY_MS = 5000;
  var PERCENT = CFG.percent || 50;
  var STATE_KEY = "apykidz_flash";
  var NEXT_KEY = "apykidz_flash_next";
  var DECLINED_KEY = "apykidz_flash_declined";
  var EXT_KEY = "apykidz_flash_ext";
  var PROMO_KEY = "apykidz_promo";
  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var memState = null;
  var timerEl = null;
  var claimEl = null;
  var reclaimEl = null;
  var askEl = null;
  var askTimer = null;
  var memExt = false;
  var memDeclined = false;
  var modalEl = null;
  var tickHandle = null;
  var observer = null;
  var pending = null;
  var busy = false;

  /* ---------- storage ---------- */
  function readState() {
    try { return JSON.parse(localStorage.getItem(STATE_KEY)) || memState; } catch (e) { return memState; }
  }
  function writeState(s) {
    memState = s;
    try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch (e) {}
  }
  var memNext = 0;
  function tooSoon() {
    var n = memNext;
    try { n = parseInt(localStorage.getItem(NEXT_KEY), 10) || n; } catch (e) {}
    return Date.now() < n;
  }
  function scheduleNext(fromMs) { holdNextUntil((fromMs || Date.now()) + AUTO_GAP_MS); }
  function holdNextUntil(ms) {
    var n = memNext;
    try { n = parseInt(localStorage.getItem(NEXT_KEY), 10) || n; } catch (e) {}
    memNext = Math.max(n, ms);
    try { localStorage.setItem(NEXT_KEY, String(memNext)); } catch (e) {}
  }
  function isExt() {
    try { return localStorage.getItem(EXT_KEY) === "1" || memExt; } catch (e) { return memExt; }
  }
  function setExt(v) {
    memExt = v;
    try { if (v) localStorage.setItem(EXT_KEY, "1"); else localStorage.removeItem(EXT_KEY); } catch (e) {}
  }
  function offerMinutes() { return isExt() ? EXT_MINUTES : MINUTES; }
  function isDeclined() {
    try { return localStorage.getItem(DECLINED_KEY) === "1" || memDeclined; } catch (e) { return memDeclined; }
  }
  function setDeclined(v) {
    memDeclined = v;
    try { if (v) localStorage.setItem(DECLINED_KEY, "1"); else localStorage.removeItem(DECLINED_KEY); } catch (e) {}
  }
  function setPromo() {
    try { sessionStorage.setItem(PROMO_KEY, JSON.stringify({ code: CODE, label: "Flash offer" })); } catch (e) {}
  }
  function clearPromo() {
    try {
      var p = JSON.parse(sessionStorage.getItem(PROMO_KEY));
      if (p && p.code === CODE) sessionStorage.removeItem(PROMO_KEY);
    } catch (e) {}
  }

  /* Anti-tamper: the stored offer must be ours, no longer than the longest
     offer we ever grant, and must not start or end unreasonably far ahead. */
  function validState(s) {
    if (!s || s.code !== CODE) return false;
    var now = Date.now();
    var max = (s.ext ? EXT_MINUTES : MINUTES) * 60 * 1000 + 5000;
    var span = s.end - s.start;
    return isFinite(s.start) && isFinite(s.end) && span > 0 && span <= max &&
      s.start <= now + 60000 && s.end <= now + max;
  }
  function isActive() {
    var s = readState();
    return !!(s && !s.expired && validState(s) && Date.now() < s.end);
  }
  function refreshCart() {
    if (window.ApykidzCart && window.ApykidzCart.render) window.ApykidzCart.render();
  }

  /* ---------- prices ---------- */
  var PRICE_SEL = ".price, .search-result-price";
  var NUM_RE = /(\d[\d,]*(?:\.\d+)?)/;

  function halve(text) {
    var m = NUM_RE.exec(text);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ""));
    if (!isFinite(n) || n <= 0) return null;
    var next = (Math.round(Math.round(n * 100) * (100 - PERCENT) / 100) / 100).toFixed(2);
    return text.replace(m[1], next);
  }
  function inOffer(el) { return el.closest(".flash-timer, .flash-modal, .flash-claim"); }

  function transformPrices(animate) {
    if (!isActive()) return;
    if (observer) observer.disconnect();
    document.querySelectorAll(PRICE_SEL).forEach(function (el) {
      if (inOffer(el) || el.querySelector(".flash-new")) return;
      var orig = el.textContent.trim();
      var discounted = halve(orig);
      if (!discounted) return;
      el.setAttribute("data-flash-orig", orig);
      el.classList.add("is-flash");
      el.innerHTML = "";
      var oldEl = document.createElement("span");
      oldEl.className = "flash-old"; oldEl.textContent = orig;
      var newEl = document.createElement("span");
      newEl.className = "flash-new"; newEl.textContent = discounted;
      var tag = document.createElement("span");
      tag.className = "flash-tag"; tag.textContent = "-" + PERCENT + "%";
      el.appendChild(oldEl); el.appendChild(newEl); el.appendChild(tag);
      if (animate) {
        el.classList.remove("flash-pop");
        void el.offsetWidth;
        el.classList.add("flash-pop");
      }
    });
    observe();
  }
  function revertPrices() {
    if (observer) observer.disconnect();
    document.querySelectorAll("[data-flash-orig]").forEach(function (el) {
      el.textContent = el.getAttribute("data-flash-orig");
      el.removeAttribute("data-flash-orig");
      el.classList.remove("is-flash", "flash-pop");
    });
  }
  function observe() {
    if (!document.body || !isActive()) return;
    if (!observer) {
      observer = new MutationObserver(function () {
        if (pending) return;
        pending = window.requestAnimationFrame(function () { pending = null; transformPrices(true); });
      });
    }
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* ---------- countdown widget ---------- */
  function buildTimer() {
    if (timerEl) return timerEl;
    timerEl = document.createElement("div");
    timerEl.className = "flash-timer";
    timerEl.setAttribute("role", "timer");
    timerEl.innerHTML =
      '<span class="flash-timer__icon" aria-hidden="true">⏰</span>' +
      '<span class="flash-timer__text"><span class="flash-timer__label">' + PERCENT + '% off ends in</span>' +
      '<span class="flash-timer__time" data-flash-time>--:--</span></span>' +
      '<span class="flash-timer__off">-' + PERCENT + '%</span>';
    document.body.appendChild(timerEl);
    return timerEl;
  }
  function fmt(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
    var mm = (m < 10 ? "0" : "") + m, ss = (s < 10 ? "0" : "") + s;
    return h > 0 ? h + ":" + mm + ":" + ss : mm + ":" + ss;
  }
  function tick() {
    var s = readState();
    if (!s || s.expired) return stopTick();
    var left = s.end - Date.now();
    if (left <= 0 || !validState(s)) return expire();
    if (timerEl) {
      var t = timerEl.querySelector("[data-flash-time]");
      if (t) t.textContent = fmt(left);
      timerEl.classList.toggle("is-urgent", left < 5 * 60 * 1000);
    }
  }
  function startTick() {
    stopTick();
    tick();
    tickHandle = window.setInterval(tick, 1000);
  }
  function stopTick() {
    if (tickHandle) { window.clearInterval(tickHandle); tickHandle = null; }
  }

  /* ---------- "Claim my discount" button (after a refusal) ---------- */
  function buildClaim() {
    if (claimEl) return claimEl;
    claimEl = document.createElement("button");
    claimEl.type = "button";
    claimEl.className = "flash-claim";
    claimEl.innerHTML =
      '<span class="flash-claim__icon" aria-hidden="true">🎁</span>' +
      '<span class="flash-claim__text"><span class="flash-claim__label">Flash offer</span>' +
      '<span class="flash-claim__cta">Claim my ' + PERCENT + '% discount now</span></span>';
    claimEl.addEventListener("click", function () {
      if (busy || isActive()) return;
      targetUrl = null;
      openModal(null, true);
    });
    document.body.appendChild(claimEl);
    return claimEl;
  }
  function showClaim() {
    if (isActive()) return;
    buildClaim();
    window.requestAnimationFrame(function () { claimEl.classList.add("is-shown"); });
  }
  function hideClaim() {
    if (claimEl) claimEl.classList.remove("is-shown");
  }

  /* ---------- "Reclaim" button (after an offer has ended) ---------- */
  function buildReclaim() {
    if (reclaimEl) return reclaimEl;
    reclaimEl = document.createElement("button");
    reclaimEl.type = "button";
    reclaimEl.className = "flash-claim flash-claim--reclaim";
    reclaimEl.innerHTML =
      '<span class="flash-claim__icon" aria-hidden="true">⏳</span>' +
      '<span class="flash-claim__text"><span class="flash-claim__label">Offer ended</span>' +
      '<span class="flash-claim__cta">Reclaim my ' + PERCENT + '% discount</span></span>';
    reclaimEl.addEventListener("click", function () {
      if (busy || isActive()) return;
      openAsk();
    });
    document.body.appendChild(reclaimEl);
    return reclaimEl;
  }
  function showReclaim() {
    if (isActive() || isDeclined()) return;
    buildReclaim();
    window.requestAnimationFrame(function () { reclaimEl.classList.add("is-shown"); });
  }
  function hideReclaim() {
    if (reclaimEl) reclaimEl.classList.remove("is-shown");
  }

  /* ---------- extension request (Ask for discount -> confirm -> sent) ---------- */
  function buildAsk() {
    if (askEl) return askEl;
    askEl = document.createElement("div");
    askEl.className = "flash-modal flash-ask";
    askEl.innerHTML =
      '<div class="flash-modal__backdrop"></div>' +
      '<div class="flash-modal__card" role="dialog" aria-modal="true" aria-labelledby="flash-ask-title">' +
        '<span class="flash-modal__ribbon">Flash offer</span>' +
        '<div data-ask-view="intro">' +
          '<h2 class="flash-modal__title" id="flash-ask-title">Your <em>' + PERCENT + '% OFF</em> offer has ended</h2>' +
          '<p class="flash-modal__sub">Want a little more time to enjoy it?</p>' +
          '<div class="flash-modal__actions">' +
            '<button type="button" class="flash-modal__accept" data-ask-go>Ask for discount</button>' +
            '<button type="button" class="flash-modal__decline" data-ask-close>Close</button>' +
          '</div>' +
        '</div>' +
        '<div data-ask-view="confirm" hidden>' +
          '<h2 class="flash-modal__title">Do you want to request an extension to enjoy the discount?</h2>' +
          '<div class="flash-modal__actions">' +
            '<button type="button" class="flash-modal__accept" data-ask-yes>Yes</button>' +
            '<button type="button" class="flash-modal__decline" data-ask-no>No</button>' +
          '</div>' +
        '</div>' +
        '<div data-ask-view="sent" hidden>' +
          '<h2 class="flash-modal__title">Your request has been received.</h2>' +
          '<p class="flash-modal__sub">We\'re reviewing it, please wait a moment.</p>' +
          '<span class="flash-ask__spinner" aria-hidden="true"></span>' +
        '</div>' +
      '</div>';
    document.body.appendChild(askEl);
    askEl.querySelector("[data-ask-go]").addEventListener("click", function () { showAskView("confirm"); });
    askEl.querySelector("[data-ask-yes]").addEventListener("click", requestExtension);
    askEl.querySelector("[data-ask-no]").addEventListener("click", closeAsk);
    askEl.querySelector("[data-ask-close]").addEventListener("click", closeAsk);
    askEl.querySelector(".flash-modal__backdrop").addEventListener("click", closeAsk);
    return askEl;
  }
  function showAskView(name) {
    askEl.querySelectorAll("[data-ask-view]").forEach(function (v) {
      v.hidden = v.getAttribute("data-ask-view") !== name;
    });
    var focusSel = { intro: "[data-ask-go]", confirm: "[data-ask-yes]" }[name];
    var btn = focusSel && askEl.querySelector(focusSel);
    if (btn) window.setTimeout(function () { btn.focus({ preventScroll: true }); }, 40);
  }
  function askOpen() { return !!(askEl && askEl.classList.contains("is-open")); }
  function askLocked() { return !!askTimer; }
  function openAsk() {
    if (isActive()) return;
    buildAsk();
    showAskView("intro");
    document.body.classList.add("flash-lock");
    askEl.classList.add("is-open");
  }
  function closeAsk() {
    if (!askEl || askLocked()) return;
    askEl.classList.remove("is-open");
    document.body.classList.remove("flash-lock");
  }
  function requestExtension() {
    if (askLocked() || isActive()) return;
    setExt(true);
    hideReclaim();
    showAskView("sent");
    askTimer = window.setTimeout(function () {
      askTimer = null;
      askEl.classList.remove("is-open");
      document.body.classList.remove("flash-lock");
      targetUrl = null;
      openModal(null, true);
    }, ASK_DELAY_MS);
  }

  function activateUi(hero) {
    document.documentElement.classList.add("flash-on");
    buildTimer();
    if (hero) timerEl.classList.add("is-hero");
    window.requestAnimationFrame(function () { timerEl.classList.add("is-shown"); });
    startTick();
    transformPrices(true);
    refreshCart();
  }

  function expire() {
    var s = readState() || {};
    s.expired = true;
    writeState(s);
    stopTick();
    clearPromo();
    document.documentElement.classList.remove("flash-on");
    if (observer) observer.disconnect();
    revertPrices();
    if (timerEl) { timerEl.classList.remove("is-shown"); }
    refreshCart();
    if (window.showToast) window.showToast("Your " + PERCENT + "% flash offer has ended.");
    showReclaim();
  }

  /* ---------- fireworks ---------- */
  var COLORS = ["#ff2f95", "#ffc82c", "#7c2bc9", "#2590f2", "#ffffff", "#ff8fc9", "#4caf7d"];
  function fireworks(durationMs) {
    if (REDUCED) return;
    var canvas = document.createElement("canvas");
    canvas.className = "flash-fireworks";
    canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(canvas);
    var ctx = canvas.getContext("2d");
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = window.innerWidth, H = window.innerHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    var rockets = [], sparks = [];
    var start = performance.now(), last = start, nextLaunch = 0;

    function launch() {
      var x = W * (0.15 + Math.random() * 0.7);
      rockets.push({
        x: x, y: H + 10,
        vx: (Math.random() - 0.5) * 60,
        vy: -(H * (0.9 + Math.random() * 0.35)),
        targetY: H * (0.15 + Math.random() * 0.35),
        color: COLORS[(Math.random() * COLORS.length) | 0]
      });
    }
    function burst(x, y, color) {
      var n = 70 + ((Math.random() * 30) | 0);
      for (var i = 0; i < n; i++) {
        var a = (Math.PI * 2 * i) / n + Math.random() * 0.15;
        var sp = 90 + Math.random() * 230;
        sparks.push({
          x: x, y: y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
          life: 1, decay: 0.55 + Math.random() * 0.5,
          color: Math.random() < 0.3 ? "#ffffff" : color, r: 1.6 + Math.random() * 1.6
        });
      }
    }

    function frame(now) {
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      var elapsed = now - start;
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";

      if (elapsed < durationMs - 900 && elapsed >= nextLaunch) {
        launch();
        if (Math.random() < 0.6) launch();
        nextLaunch = elapsed + 260 + Math.random() * 200;
      }
      rockets = rockets.filter(function (r) {
        r.x += r.vx * dt; r.y += r.vy * dt; r.vy += 380 * dt;
        ctx.beginPath(); ctx.arc(r.x, r.y, 2.4, 0, 6.283); ctx.fillStyle = "#fff"; ctx.fill();
        if (r.y <= r.targetY || r.vy >= -40) { burst(r.x, r.y, r.color); return false; }
        return true;
      });
      sparks = sparks.filter(function (p) {
        p.vx *= 0.985; p.vy = p.vy * 0.985 + 150 * dt;
        p.x += p.vx * dt; p.y += p.vy * dt; p.life -= p.decay * dt;
        if (p.life <= 0) return false;
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * (0.5 + p.life * 0.5), 0, 6.283);
        ctx.fillStyle = p.color; ctx.fill();
        return true;
      });
      ctx.globalAlpha = 1;

      if (elapsed < durationMs || sparks.length || rockets.length) {
        window.requestAnimationFrame(frame);
      } else if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    }
    window.requestAnimationFrame(frame);
  }

  /* ---------- popup ---------- */
  function productFromLink(link) {
    var path = link.pathname || "";
    var m = /\/products\/([^\/?#]+)/.exec(path);
    var slug = m ? decodeURIComponent(m[1]) : null;
    var data = null;
    if (slug && window.APYKIDZ_PRODUCTS) {
      for (var i = 0; i < window.APYKIDZ_PRODUCTS.length; i++) {
        if (window.APYKIDZ_PRODUCTS[i].slug === slug) { data = window.APYKIDZ_PRODUCTS[i]; break; }
      }
    }
    var img = link.querySelector("img");
    if (!img && slug) {
      var card = link.closest(".product-grid-item, .product-card, .featured-spotlight, li, article");
      img = card && card.querySelector("img");
    }
    var src = img && (img.currentSrc || img.src);
    if (!src && data && data.image) src = data.image;
    return {
      isProduct: !!slug,
      title: data ? data.title : (link.getAttribute("aria-label") || ""),
      image: src || null,
      emoji: data && data.emoji ? data.emoji : "🎁"
    };
  }

  function buildModal() {
    if (modalEl) return modalEl;
    modalEl = document.createElement("div");
    modalEl.className = "flash-modal";
    modalEl.innerHTML =
      '<div class="flash-modal__backdrop"></div>' +
      '<div class="flash-modal__card" role="dialog" aria-modal="true" aria-labelledby="flash-title">' +
        '<span class="flash-modal__ribbon">Flash offer</span>' +
        '<div class="flash-modal__product" data-flash-product>' +
          '<span class="flash-modal__thumb" data-flash-thumb></span>' +
          '<span class="flash-modal__product-text"><small>You are looking at</small><strong data-flash-name></strong></span>' +
        '</div>' +
        '<h2 class="flash-modal__title" id="flash-title" data-flash-title></h2>' +
        '<p class="flash-modal__sub">This offer ends in just a few minutes.</p>' +
        '<div class="flash-modal__actions">' +
          '<button type="button" class="flash-modal__accept" data-flash-accept>Claim my ' + PERCENT + '% off</button>' +
          '<button type="button" class="flash-modal__decline" data-flash-decline>No thanks, continue</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modalEl);
    modalEl.querySelector("[data-flash-accept]").addEventListener("click", accept);
    modalEl.querySelector("[data-flash-decline]").addEventListener("click", decline);
    modalEl.querySelector(".flash-modal__backdrop").addEventListener("click", decline);
    return modalEl;
  }

  var targetUrl = null;

  function openModal(link, fromClaim) {
    var here = document.createElement("a");
    here.href = window.location.href;
    var info = productFromLink(link || here);
    if (!fromClaim) { scheduleNext(); setExt(false); hideReclaim(); }
    buildModal();
    var mins = offerMinutes();
    modalEl.querySelector("[data-flash-title]").innerHTML =
      "Get <em>" + PERCENT + "% OFF</em> if you order within the next " + mins + " minutes";
    var box = modalEl.querySelector("[data-flash-product]");
    var thumb = modalEl.querySelector("[data-flash-thumb]");
    var name = modalEl.querySelector("[data-flash-name]");
    thumb.innerHTML = "";
    if (info.image) {
      var im = document.createElement("img");
      im.src = info.image; im.alt = "";
      thumb.appendChild(im);
    } else {
      thumb.textContent = info.emoji;
    }
    var off = document.createElement("span");
    off.className = "flash-modal__thumb-off"; off.textContent = "-" + PERCENT + "%";
    thumb.appendChild(off);
    name.textContent = info.title || "Everything in our shop";
    box.style.display = info.isProduct || info.image ? "" : "none";
    modalEl.classList.remove("is-leaving");
    document.body.classList.add("flash-lock");
    modalEl.classList.add("is-open");
    var btn = modalEl.querySelector("[data-flash-accept]");
    window.setTimeout(function () { btn.focus({ preventScroll: true }); }, 60);
  }
  function closeModal() {
    if (!modalEl) return;
    modalEl.classList.remove("is-open", "is-leaving");
    document.body.classList.remove("flash-lock");
  }
  function go() {
    if (targetUrl) { var u = targetUrl; targetUrl = null; window.location.href = u; }
  }

  function decline() {
    if (busy) return;
    setDeclined(true);
    hideReclaim();
    showClaim();
    closeModal();
    go();
  }

  function accept() {
    if (busy) return;
    busy = true;
    var now = Date.now();
    var wasExt = isExt();
    var end = now + (wasExt ? EXT_MINUTES : MINUTES) * 60 * 1000;
    setDeclined(false);
    hideClaim();
    hideReclaim();
    writeState({ code: CODE, start: now, end: end, ext: wasExt });
    setExt(false);
    if (wasExt) holdNextUntil(end + EXT_WAIT_MS);
    setPromo();

    modalEl.classList.add("is-leaving");
    var hold = REDUCED ? 0 : 3600;
    window.setTimeout(function () {
      document.body.classList.remove("flash-lock");
      modalEl.classList.remove("is-open", "is-leaving");
    }, REDUCED ? 30 : 850);

    window.setTimeout(function () {
      fireworks(hold);
      activateUi(!REDUCED);
      if (!REDUCED) {
        window.setTimeout(function () { if (timerEl) timerEl.classList.remove("is-hero"); }, 2900);
      }
    }, REDUCED ? 0 : 420);

    window.setTimeout(function () { busy = false; go(); }, hold + (REDUCED ? 400 : 200));
  }

  /* ---------- link interception ---------- */
  function shouldIntercept(e, link) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
    if (link.target && link.target !== "_self") return false;
    if (link.hasAttribute("download") || link.hasAttribute("data-no-flash")) return false;
    if (link.origin !== window.location.origin) return false;
    if (!/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?(?:products|collections)(?:\/|$)/.test(link.pathname)) return false;
    if (link.pathname === window.location.pathname) return false;
    if (link.closest(".flash-modal, .flash-timer, .flash-claim")) return false;
    return true;
  }

  document.addEventListener("click", function (e) {
    if (isActive() || tooSoon() || busy || isExt() || askOpen() || askLocked()) return;
    var link = e.target.closest && e.target.closest("a[href]");
    if (!link || !shouldIntercept(e, link)) return;
    e.preventDefault();
    e.stopPropagation();
    targetUrl = link.href;
    openModal(link);
  }, true);

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (askOpen()) closeAsk();
    else if (modalEl && modalEl.classList.contains("is-open") && !busy) decline();
  });

  window.addEventListener("pageshow", function (e) {
    if (e.persisted) { closeModal(); busy = false; if (!askLocked()) closeAsk(); }
  });

  /* ---------- direct landing on a product page (ads, shared links) ---------- */
  var LANDING_DELAY_MS = 3000;
  function landedFromOutside() {
    if (!/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?products\/[^\/]+/.test(window.location.pathname)) return false;
    if (!document.referrer) return true;
    try { return new URL(document.referrer).origin !== window.location.origin; } catch (e) { return true; }
  }
  function maybeOfferOnLanding() {
    if (!landedFromOutside()) return;
    window.setTimeout(function () {
      if (isActive() || tooSoon() || busy || isExt() || askOpen() || askLocked()) return;
      if (modalEl && modalEl.classList.contains("is-open")) return;
      targetUrl = null;
      openModal(null);
    }, LANDING_DELAY_MS);
  }

  /* ---------- public API + boot ---------- */
  window.ApykidzFlash = { active: isActive, percent: PERCENT };

  function init() {
    var s = readState();
    if ((isDeclined() || isExt()) && !isActive()) showClaim();
    maybeOfferOnLanding();
    if (!s) return;
    if (s.expired || Date.now() >= s.end || !validState(s)) {
      if (!s.expired) expire(); else { clearPromo(); showReclaim(); }
      return;
    }
    setPromo();
    activateUi(false);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
